import { createSlice } from "@reduxjs/toolkit";
import {
  IAiSummary,
  IEligibleBusinessList,
  IPublicReport,
  IRankCompare,
  IRankScans,
  IReportData,
  IReportDetail,
  IReportList,
  IReportPermissions,
} from "../../components/ReportEditor/apiTypes";
import { rankCompareKey } from "../../components/ReportEditor/rankCompareKey";
import {
  bulkDeleteReportsAction,
  bulkScheduleReportsAction,
  bulkSendReportsAction,
  createReportAction,
  deleteReportAction,
  getAiSummaryAction,
  getEligibleBusinessesAction,
  getPublicRankCompareAction,
  getPublicReportAction,
  getRankCompareAction,
  getRankScansAction,
  getReportAction,
  getReportDataAction,
  listReportsAction,
  refreshAiSummaryAction,
  saveKeywordGroupsAction,
  saveKeywordSelectionAction,
  sendReportNowAction,
  updateEmailScheduleAction,
  updateReportAction,
} from "../actions/ReportBuilderActions";

export { rankCompareKey };

export interface AsyncSlot<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface ReportBuilderState {
  list: AsyncSlot<IReportList>;
  eligible: AsyncSlot<IEligibleBusinessList>;
  current: AsyncSlot<IReportDetail>;
  reportData: AsyncSlot<IReportData>;
  rankScans: Record<string, AsyncSlot<IRankScans>>;
  rankCompare: Record<string, AsyncSlot<IRankCompare>>;
  aiSummary: AsyncSlot<IAiSummary>;
  publicReport: AsyncSlot<IPublicReport>;
  publicRankCompare: Record<string, AsyncSlot<IRankCompare>>;
  permissions: IReportPermissions;
  isSaving: boolean;
  isSending: boolean;
  isRefreshingSummary: boolean;
}

const emptySlot = <T>(): AsyncSlot<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: ReportBuilderState = {
  list: emptySlot(),
  eligible: emptySlot(),
  current: emptySlot(),
  reportData: emptySlot(),
  rankScans: {},
  rankCompare: {},
  aiSummary: emptySlot(),
  publicReport: emptySlot(),
  publicRankCompare: {},
  permissions: { canManage: false, isOwner: false },
  isSaving: false,
  isSending: false,
  isRefreshingSummary: false,
};

const reportBuilderSlice = createSlice({
  name: "reportBuilder",
  initialState,
  reducers: {
    resetCurrentReport: (state) => {
      state.current = emptySlot();
      state.reportData = emptySlot();
      state.rankScans = {};
      state.rankCompare = {};
    },
    resetReportBuilderState: () => initialState,
  },
  extraReducers: (builder) => {
    // Each read thunk fills one slot; the rejected payload is the backend message
    const bindRead = <K extends keyof ReportBuilderState>(
      action: any,
      key: K,
      onData?: (state: ReportBuilderState, data: any) => void,
      merge?: (previous: any, next: any, arg: any) => any,
    ) => {
      builder
        .addCase(action.pending, (state) => {
          const slot = state[key] as AsyncSlot<any>;
          slot.loading = true;
          slot.error = null;
        })
        .addCase(action.fulfilled, (state, act: any) => {
          const slot = state[key] as AsyncSlot<any>;
          slot.loading = false;
          const next = act.payload?.data ?? null;
          slot.data = merge ? merge(slot.data, next, act.meta?.arg) : next;
          onData?.(state, act.payload?.data);
        })
        .addCase(action.rejected, (state, act: any) => {
          if (act.meta?.aborted) return;
          const slot = state[key] as AsyncSlot<any>;
          slot.loading = false;
          slot.error = act.payload || "Request failed";
        });
    };

    const syncPermissions = (state: ReportBuilderState, data: any) => {
      if (data?.permissions) state.permissions = data.permissions;
    };

    bindRead(listReportsAction, "list", syncPermissions);
    bindRead(getEligibleBusinessesAction, "eligible", syncPermissions);
    bindRead(getReportAction, "current", syncPermissions);
    // A fetch for named sections keeps the sections it did not ask for, so a rankings refresh never blanks the rest of the report
    bindRead(getReportDataAction, "reportData", undefined, (previous, next, arg) =>
      arg?.sections?.length
        ? previous && next
          ? { ...next, sections: { ...previous.sections, ...next.sections } }
          : previous
        : next,
    );
    // Scan history and compare results are cached per heatmap and per scan pair
    const bindKeyedRead = (
      action: any,
      key: "rankScans" | "rankCompare" | "publicRankCompare",
      keyOf: (arg: any) => string,
    ) => {
      builder
        .addCase(action.pending, (state, act: any) => {
          const id = keyOf(act.meta.arg);
          const slot = state[key][id] ?? emptySlot<any>();
          state[key][id] = { ...slot, loading: true, error: null };
        })
        .addCase(action.fulfilled, (state, act: any) => {
          state[key][keyOf(act.meta.arg)] = {
            data: act.payload?.data ?? null,
            loading: false,
            error: null,
          };
        })
        .addCase(action.rejected, (state, act: any) => {
          if (act.meta?.aborted) return;
          state[key][keyOf(act.meta.arg)] = {
            data: null,
            loading: false,
            error: act.payload || "Request failed",
          };
        });
    };

    bindKeyedRead(getRankScansAction, "rankScans", (arg) => arg.heatmapId);
    bindKeyedRead(getRankCompareAction, "rankCompare", rankCompareKey);
    bindRead(getAiSummaryAction, "aiSummary");
    bindRead(getPublicReportAction, "publicReport");
    bindKeyedRead(getPublicRankCompareAction, "publicRankCompare", rankCompareKey);

    // Autosave of keyword groups merges only the rank block, so updatedAt stays and the editor does not reseed unsaved layout edits
    builder.addCase(saveKeywordGroupsAction.fulfilled, (state, action) => {
      const rank = action.payload?.data?.rank;
      if (rank && state.current.data) state.current.data.rank = rank;
    });

    builder.addCase(saveKeywordSelectionAction.fulfilled, (state, action) => {
      const rank = action.payload?.data?.rank;
      if (rank && state.current.data) state.current.data.rank = rank;
    });

    // Same reason as above: merge only the email block so a schedule save never reseeds the editor
    builder.addCase(updateEmailScheduleAction.fulfilled, (state, action) => {
      const email = action.payload?.data?.email;
      if (email && state.current.data) state.current.data.email = email;
    });

    builder.addCase(updateReportAction.fulfilled, (state, action) => {
      const data = action.payload?.data;
      if (data) {
        state.current.data = data;
        if (state.publicReport.data?.settings && data.customSections) {
          state.publicReport.data.settings.customSections = data.customSections;
        }
      }
    });

    builder.addCase(refreshAiSummaryAction.fulfilled, (state, action) => {
      const data = action.payload?.data;
      if (data) state.aiSummary.data = data;
    });

    const flag = (
      actions: any[],
      key: "isSaving" | "isSending" | "isRefreshingSummary",
    ) =>
      builder
        .addMatcher(
          (action: any) =>
            actions.some((thunk) => thunk.pending.match(action)),
          (state) => {
            state[key] = true;
          },
        )
        .addMatcher(
          (action: any) =>
            actions.some(
              (thunk) =>
                thunk.fulfilled.match(action) || thunk.rejected.match(action),
            ),
          (state) => {
            state[key] = false;
          },
        );

    flag(
      [
        createReportAction,
        updateReportAction,
        deleteReportAction,
        bulkDeleteReportsAction,
        updateEmailScheduleAction,
        bulkScheduleReportsAction,
      ],
      "isSaving",
    );
    flag([sendReportNowAction, bulkSendReportsAction], "isSending");
    flag([refreshAiSummaryAction], "isRefreshingSummary");
  },
});

export const { resetCurrentReport, resetReportBuilderState } =
  reportBuilderSlice.actions;
export default reportBuilderSlice.reducer;
